import { useEffect, useState, useCallback } from 'react';

const VERSION_HEADER = 'x-api-v2-version';

// Enhanced SSR detection
const isSSR = () => {
	return (
		typeof window === 'undefined' ||
		typeof document === 'undefined' ||
		typeof sessionStorage === 'undefined' ||
		process.env.NODE_ENV === 'test'
	);
};

interface UseVersionPopupOptions {
	onPopupShow?: () => void;
	onPopupHide?: () => void;
}

interface UseVersionPopupReturn {
	isOpen: boolean;
	handleClose: () => void;
}

/**
 * Custom hook for managing version popup state and events
 * Handles sessionStorage events, custom events, and popup state management
 */
export const useVersionPopup = (options: UseVersionPopupOptions = {}): UseVersionPopupReturn => {
	const { onPopupShow, onPopupHide } = options;
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		if (isSSR()) {
			return;
		}
		// Handles cross-tab storage events
		const handleStorageChange = ({ key, newValue, oldValue }: StorageEvent) => {
			if (key !== VERSION_HEADER) {
				return;
			}
			if (!oldValue || newValue === oldValue) {
				return;
			}

			setIsOpen(true);
			onPopupShow?.();
		};
		const controller = new AbortController();
		window.addEventListener('storage', handleStorageChange, { signal: controller.signal });
		return () => controller.abort();
	}, [onPopupShow]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		onPopupHide?.();

		// Reload the page
		window.location.reload();
	}, [onPopupHide]);

	return { isOpen, handleClose };
};
